import React from 'react';

import classNames from 'classnames';

import { formatMoney } from '../../budget_overview/budgetOverviewFormat';
import BudgetOverviewInfoTip from '../../budget_overview/BudgetOverviewInfoTip';
import { kpiDefinitions } from '../KpiTiles';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';

/** One line per column, read by the header (i) tips. */
export const columnDefinitions = (fx = null) => ({
    project: { label: $t('Project'), text: $t('Each selected project, on the budget version chosen in the picker.') },
    category: { label: $t('Department / cost centre'), text: $t('Revenue categories and expenditure departments, matched by name across projects.') },
    ...kpiDefinitions(),
    committed: { label: $t('Committed'), text: $t('Committed to date, from the latest estimate.') },
    actuals: { label: $t('Actuals'), text: $t('Reconciled actual cost to date, excluding tax.') },
    remaining: { label: $t('Remaining'), text: $t('Expenditure less committed less actuals.') },
    total: {
        label: $t('Total'),
        text: fx
            ? $t('Sum across the selected projects in {{currency}}, leaving out any project with no rate.', { currency: fx.show_in })
            : $t('Sum across the selected projects, without currency conversion.'),
    },
    rate: {
        label: $t('Rate'),
        text: $t("Each project's rate against the anchor currency, common or locked. Figures are converted through the anchor when the report is drawn."),
    },
});

// On an inner wrapper rather than the cell: the table's own cell padding
// outranks a utility class on the <td>.
export const LEVEL_INDENT = ['', 'tw-ps-6', 'tw-ps-12'];

export function Toggle({ open, label, onClick }) {
    const Icon = open ? ChevronDownIcon : ChevronRightIcon;

    return (
        <button
            type="button"
            onClick={onClick}
            aria-expanded={open}
            aria-label={open ? $t('Collapse {{name}}', { name: label }) : $t('Expand {{name}}', { name: label })}
            className="tw-inline-flex"
        >
            <Icon className="tw-w-2.5 tw-h-2.5" />
        </button>
    );
}

/**
 * The label of a row: indented one step per level, with a fixed-width slot
 * for the expand control so a name lines up whether or not its row has one.
 */
export function RowLabel({ level, toggle = null, className, children }) {
    return (
        <div className={classNames('tw-flex tw-items-center', LEVEL_INDENT[level], className)}>
            <span className="tw-inline-flex tw-w-4 tw-shrink-0 tw-items-center">{toggle}</span>
            {children}
        </div>
    );
}

/**
 * A money cell. `semantic` colours a gain green and a loss red; otherwise
 * only a negative is red. On the filled `footer` a gain stays white and a
 * loss is a light red that still reads on the blue.
 */
export function MoneyCell({ value, locale, semantic = false, footer = false, className }) {
    const blank = value === null || value === undefined || Number(value) === 0;
    const loss = footer ? 'tw-text-red-25' : 'tw-text-red';
    const colour = blank ? null : Number(value) < 0 ? loss : semantic && !footer ? 'tw-text-green' : null;

    return <td className={classNames('inbud-numeric-cell', colour, className)}>{formatMoney(value, locale)}</td>;
}

export function HeaderCell({ definition, numeric = true, className, children }) {
    return (
        <th className={classNames(numeric ? '!tw-text-end' : '!tw-text-start', className)}>
            <span className="tw-inline-flex tw-items-center tw-gap-1">
                {children || definition.label}
                <BudgetOverviewInfoTip label={definition.label} text={definition.text} />
            </span>
        </th>
    );
}

/** The label of a total, naming its currency only when there is just one. */
export function totalSuffix(report) {
    if (report.mixed_currency) {
        return $t('(mixed currencies)');
    }

    return report.currency ? `(${report.currency})` : '';
}
